import { trackSkaiRequestServed, trackSkaiOutOfTokens, trackSkaiFreeReset } from "./pendo";

// ─── OD-1230 SkAi token analytics helpers ──────────────────────────────────────
// The AI endpoints return a `skaiTokens` object on both success and the 403
// out-of-tokens rejection, so every event below is derived from a response the app
// already has — no extra network calls, and nothing here can break a user flow.

// Fires skai_request_served with the per-bucket split for a successful request.
export const reportSkaiSpend = (skaiTokens, surface) => {
  if (!skaiTokens) return;
  trackSkaiRequestServed({
    surface,
    unlimited: !!skaiTokens.unlimited,
    charged: skaiTokens.charged ?? null,
    from_free: skaiTokens.fromFree ?? 0,
    from_purchased: skaiTokens.fromPurchased ?? 0,
    free_balance_after: skaiTokens.freeBalance ?? null,
    purchased_balance_after: skaiTokens.purchasedBalance ?? null,
    total_balance_after: skaiTokens.total ?? null,
    // Spanning both buckets in one request is the interesting case for the funnel.
    spanned_buckets: (skaiTokens.fromFree ?? 0) > 0 && (skaiTokens.fromPurchased ?? 0) > 0,
  });
};

// Fires skai_out_of_tokens when the request was refused for lack of balance. Any
// other failure (network, 500, PHI validation) is not a token event and is ignored.
export const reportSkaiRejection = (error, surface) => {
  const status = error?.response?.status;
  const message = String(error?.response?.data?.message || "").toLowerCase();
  if (status !== 403 || !message.includes("token quota")) return;
  const skaiTokens = error?.response?.data?.skaiTokens || {};
  trackSkaiOutOfTokens({
    surface,
    required: skaiTokens.required ?? null,
    free_balance: skaiTokens.freeBalance ?? 0,
    purchased_balance: skaiTokens.purchasedBalance ?? 0,
    total_balance: skaiTokens.total ?? 0,
  });
};

// ─── Monthly free reset detection ──────────────────────────────────────────────
// The reset happens in a backend cron with no user session, so it can't emit a
// client event at the moment it occurs. Instead, compare the `freeResetAt` the API
// reports against the last value this browser saw: a change means the refill has
// happened since the user was last here. Fires at most once per reset per browser.
const RESET_SEEN_KEY = "skai:lastFreeResetAt";

export const reportSkaiFreeResetIfNew = (bucketRow) => {
  const resetAt = bucketRow?.freeResetAt;
  if (!resetAt) return;
  try {
    const seen = window.localStorage.getItem(RESET_SEEN_KEY);
    const current = new Date(resetAt).toISOString();
    if (seen === current) return;
    window.localStorage.setItem(RESET_SEEN_KEY, current);
    // No event on the very first observation — there's no prior value to prove a
    // reset happened, and firing here would just report "user opened the app".
    if (!seen) return;
    trackSkaiFreeReset({
      reset_at: current,
      free_balance: bucketRow?.freeBalance ?? null,
      free_allocation: bucketRow?.freeAllocation ?? null,
      purchased_balance: bucketRow?.purchasedBalance ?? null,
    });
  } catch {
    // localStorage unavailable (private mode / quota) — analytics only, skip it.
  }
};
